import { useMutation } from 'react-query';
import { PENDING_TIMESHEETS, TIMESHEET_PACKAGE } from 'api/use-query-constants';
import axios, { AxiosResponse } from 'axios';
import { clearChangedTimeEntries } from '../redux/time-entries-slice';
import { PendingTimesheet } from '../models/pending-timesheets.model';
import { setChangedPendingTimesheets, setPendingTimesheets } from '../redux/pending-timesheets-slice';
import { IOnlineApprovalTimeEntry } from '../models/online-approval-time-entry.model';
import { useTrackCotapEvent } from '../../app-insights/track-event.hook';
import { useDispatch, useSelector } from 'react-redux';
import { setDisablingSpinnerVisible } from 'redux/global-elements-slice';
import { RootState } from '../../../store/root-reducer';
import { AppConfig } from '../../config/config.model';
import { openModal } from '../../../redux/message-modal-slice';
import { getQueryClient } from '../../config/config';
import { TimesheetPackage } from '../models/timesheet-package.model';

interface IEditTimeEntriesVariables {
    newEntries: IOnlineApprovalTimeEntry[];
    timesheetId: string;
    supervisorComments: string;
    updatedTimesheets: PendingTimesheet[];
    timesheets: PendingTimesheet[];
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useEditTimeEntriesMutation(
    url: string,
    onSuccess: () => void = () => {
        /* do nothing */
    },
) {
    const trackError = useTrackCotapEvent('SupervisorEditTimeEntry Error', false);
    const trackRollbackError = useTrackCotapEvent('SupervisorEditTimeEntry rollback Error', false);
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const isUsingV2Endpoints = useSelector((state: RootState) => state.globalElements.isUsingV2Endpoints);
    const dispatch = useDispatch();
    const queryClient = getQueryClient();

    const { mutate, isLoading: isEditTimeEntriesLoading } = useMutation(
        ({ newEntries, timesheetId, supervisorComments }: IEditTimeEntriesVariables) => {
            const entries = newEntries.map((entry: IOnlineApprovalTimeEntry) => {
                const newEntry = { ...entry };
                delete newEntry.isNew;
                delete newEntry.isInvalid;
                delete newEntry.hasChanges;
                return newEntry;
            });

            if (isUsingV2Endpoints) {
                return (
                    axios
                        .post(`${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/v2/SupervisorEditTimeEntry`, {
                            cotaSecret: url,
                            timesheetId,
                            supervisorComments,
                            onlineApprovalTimeEntries: entries,
                        })
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .then((response: AxiosResponse<any>) => response?.data)
                        .catch((e: Error) => {
                            trackError({ e });
                            throw e;
                        })
                        .finally(() => {
                            dispatch(setDisablingSpinnerVisible(false));
                        })
                );
            }

            return (
                axios
                    .post(
                        `${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/SupervisorEditTimeEntry?timesheetId=${timesheetId}&supervisorComments=${supervisorComments}&clientOnlineTimesheetApprovalSecret=${url}`,
                        entries,
                    )
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((response: AxiosResponse<any>) => response?.data)
                    .catch((e: Error) => {
                        trackError({ e });
                        throw e;
                    })
                    .finally(() => {
                        dispatch(setDisablingSpinnerVisible(false));
                    })
            );
        },
        {
            onMutate: ({ newEntries, updatedTimesheets }: IEditTimeEntriesVariables) => {
                //cancel any outgoing refetches
                queryClient.cancelQueries(TIMESHEET_PACKAGE);
                queryClient.cancelQueries(PENDING_TIMESHEETS);

                //snapshot previous values
                const previousTimesheetPackage = queryClient.getQueryData(TIMESHEET_PACKAGE) as TimesheetPackage;
                const previousTimesheets = queryClient.getQueryData(PENDING_TIMESHEETS);

                const newTimesheetPackage = {
                    ...previousTimesheetPackage,
                    onlineApprovalTimeEntries: newEntries,
                } as TimesheetPackage;

                // Optimistically update to the new value
                queryClient.setQueryData(TIMESHEET_PACKAGE, newTimesheetPackage);
                queryClient.setQueryData(PENDING_TIMESHEETS, updatedTimesheets);
                dispatch(setPendingTimesheets(updatedTimesheets));
                dispatch(setChangedPendingTimesheets(updatedTimesheets));

                return () => {
                    queryClient.setQueryData(TIMESHEET_PACKAGE, previousTimesheetPackage);
                    queryClient.setQueryData(PENDING_TIMESHEETS, previousTimesheets);
                };
            },

            onSuccess,

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (err: any, variables: IEditTimeEntriesVariables, rollback: () => void) => {
                rollback();
                setPendingTimesheets(variables.timesheets);
                setChangedPendingTimesheets(variables.timesheets);

                dispatch(
                    openModal({
                        title: 'Error saving timesheet',
                        body: 'Please contact Medical Solutions',
                    }),
                );

                trackRollbackError({ err });
            },
            onSettled: () => {
                clearChangedTimeEntries();
            },
        },
    );

    return { mutate, isEditTimeEntriesLoading };
}
